// Where this tab's visit came from, sent as `entry` on event, ticket, shop and news and radio
// preview page views for the event Overview's Top sources
// (docs/product/events/event-funnel-tracking.md#landing-source). Read once per full page load, at
// module scope (imported by pages/_app and app/layout), because client-side navigation drops the
// query ("Get tickets" keeps only the path) and never changes document.referrer. Kept in
// sessionStorage so it survives reloads and payment redirects in the same tab.

import { SHARE_CODE } from "./share"

interface LandingEntry {
  referrerHost?: string
  referrerApp?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  share?: string
  browser?: string
}

const storageKey = "werz:landing:v1"

const UTM_PARAMS = [
  ["utm_source", "utmSource"],
  ["utm_medium", "utmMedium"],
  ["utm_campaign", "utmCampaign"],
  ["utm_content", "utmContent"],
] as const

// Explicit in-app browser signatures only; a plain Safari or Chrome stays unclassified.
const inAppBrowser = (userAgent: string) => {
  if (/Instagram/i.test(userAgent)) return "instagram"
  if (/FBAN|FBAV|FB_IAB|FBIOS/.test(userAgent)) return "facebook"
  if (/Snapchat/i.test(userAgent)) return "snapchat"
  if (/musical_ly|BytedanceWebview|TikTok/i.test(userAgent)) return "tiktok"
  return undefined
}

// The host only, never the URL. An Android app opening the link reports android-app://<package>,
// which Chromium before 130 parses without a host.
const referrerOf = (referrer: string): Pick<LandingEntry, "referrerHost" | "referrerApp"> => {
  const app = /^android-app:\/\/([^/?#]+)/i.exec(referrer)?.[1]
  if (app) return { referrerApp: app.toLowerCase() }
  try {
    const url = new URL(referrer)
    const host = url.hostname.toLowerCase()
    if (!host) return {}
    return url.protocol === "http:" || url.protocol === "https:" ? { referrerHost: host } : {}
  } catch {
    return {}
  }
}

// The API's rules (api/src/utils/user-activity/entry.ts), so the landing is never refused or cut
// differently: no control or format characters, at most 200 characters, no half emoji.
// biome-ignore lint/complexity/useRegexLiterals: the es5 TypeScript target rejects the u flag in a literal; every supported browser has it
const UNPRINTABLE = new RegExp("[\\p{Cc}\\p{Cf}]", "gu")
// biome-ignore lint/complexity/useRegexLiterals: as above
const LONE_SURROGATE = new RegExp("\\p{Cs}", "gu")
const tagValue = (value: string | null) =>
  value?.replace(UNPRINTABLE, "").trim().slice(0, 200).replace(LONE_SURROGATE, "").trim() ||
  undefined

// Our own pages, and Stripe's customer portal, which returns to the shop page.
const isOwnHost = (host: string) =>
  host === window.location.hostname ||
  host === "werz.at" ||
  host.endsWith(".werz.at") ||
  host === "stripe.com" ||
  host.endsWith(".stripe.com")

let inMemory: LandingEntry | null = null

const readLanding = (): LandingEntry | null => {
  try {
    const stored = sessionStorage.getItem(storageKey)
    return stored ? (JSON.parse(stored) as LandingEntry) : inMemory
  } catch {
    return inMemory
  }
}

const writeLanding = (entry: LandingEntry) => {
  inMemory = entry
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(entry))
  } catch {
    // Storage blocked or full: the in-memory copy covers this page load, and an older stored
    // landing must not outlive it.
    try {
      sessionStorage.removeItem(storageKey)
    } catch {}
  }
}

// A load with an external referrer, UTM values or a share code replaces the landing; an own-domain
// or empty referrer keeps it (the event page → tickets step). A reload or back/forward keeps it
// too: the browser repeats the original referrer while the URL has lost its query. A payment
// provider returning to a checkout page is never a landing, so no payment-domain list is needed.
const recordLanding = () => {
  if (/\/checkout(-[a-z]+)?\/?$/.test(window.location.pathname)) return
  const navigation = performance.getEntriesByType?.("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined
  const repeated = navigation?.type === "reload" || navigation?.type === "back_forward"
  if (repeated && readLanding()) return
  const params = new URLSearchParams(window.location.search)
  const tags: LandingEntry = {}
  for (const [param, key] of UTM_PARAMS) {
    const value = tagValue(params.get(param))
    if (value) tags[key] = value
  }
  const share = params.get("s")?.trim()
  if (share && SHARE_CODE.test(share)) tags.share = share
  const referrer = referrerOf(document.referrer)
  const external =
    !!referrer.referrerApp || (!!referrer.referrerHost && !isOwnHost(referrer.referrerHost))
  if (!external && Object.keys(tags).length === 0 && readLanding()) return
  writeLanding({ ...(external ? referrer : {}), ...tags })
}

if (typeof window !== "undefined") recordLanding()

// The landing for this tab, plus the in-app browser. Always an object: an empty entry tells the API
// the visit was captured with no signal, unlike older rows without one.
export const landingEntry = (): LandingEntry => {
  const browser = typeof navigator === "undefined" ? undefined : inAppBrowser(navigator.userAgent)
  return { ...readLanding(), ...(browser ? { browser } : {}) }
}
